import React from 'react'
import { useApolloClient } from '@apollo/client'
import { pdf } from '@react-pdf/renderer'
import {
  RequestTestResultPdfUrl,
  RequestTestResultPdfUrlVariables,
  GetPDFTest,
  GetPDFTestVariables,
  Lab,
} from '@modmd/data'
import { downloadFileFromUrl } from 'utils/helpers'
import Button, { StyledButtonProps } from 'components/Button'
import Icon from 'components/Icon'
import { REQUEST_TEST_RESULT_PDF_URL, GET_PDF_TEST } from './operations'
import PDFDocument, { PDFDocumentProps } from './PDFDocument'

export interface Props extends Pick<StyledButtonProps, 'appearance' | 'size'> {
  testId: string
  shouldDownloadPdfOnMount?: boolean
}

export const DownloadTestResultPdfButton: React.VFC<Props> = ({
  appearance,
  size,
  testId,
  shouldDownloadPdfOnMount,
}) => {
  const apolloClient = useApolloClient()
  const [isLoading, setIsLoading] = React.useState(false)
  const [showButton, setShowButton] = React.useState(true)

  const handleDownloadClick = React.useCallback(
    async (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e?.stopPropagation()

      try {
        setIsLoading(true)
        const { data: testData } = await apolloClient.query<GetPDFTest, GetPDFTestVariables>({
          query: GET_PDF_TEST,
          variables: { id: testId },
        })
        const { test } = testData
        let downloadUrl
        if (test && test.result) {
          if (
            test.TestSession.lab === Lab.ModmdLab ||
            test.TestSession.lab === Lab.ModmdAtl ||
            test.TestSession.lab === Lab.DendiLax ||
            test.TestSession.lab === Lab.DendiAtl
          ) {
            const [participant] = test.ParticipantMembers
            const data: PDFDocumentProps = {
              barcode: test.barcode || '-',
              birthDate: participant.User.birthDate,
              firstName: participant.User.firstName || '-',
              lastName: participant.User.lastName || '-',
              email: participant.User.email || '-',
              gender: participant.User.gender || '',
              phoneNumber: participant.User.phoneNumber || '-',
              id: test.id || '',
              lab: test.TestSession.lab,
              result: test.result || '',
              secondResult: test.secondResult || '',
              resultReceivedAt: test.resultReceivedAt,
              specimenType: test.specimenType || 'midNasal',
              testedAt: test.testedAt,
              type: test.TestSession.type,
              Address: {
                city: participant.User?.Address?.city || '',
                state: participant.User?.Address?.state || '',
                street: participant.User?.Address?.street || '',
                zip: participant.User?.Address?.zip || '',
              },
            }

            const pdfBlob = await pdf(<PDFDocument data={data} />).toBlob()
            downloadUrl = URL.createObjectURL(pdfBlob)
          } else {
            const { data } = await apolloClient.query<
              RequestTestResultPdfUrl,
              RequestTestResultPdfUrlVariables
            >({
              query: REQUEST_TEST_RESULT_PDF_URL,
              variables: { id: testId },
            })
            downloadUrl = data?.requestTestResultPdfUrl
          }
          if (downloadUrl) {
            const sampleCollectionYear = new Date(test.testedAt).getFullYear()
            const sampleCollectionMonth = new Date(test.testedAt).getMonth() + 1
            const sampleCollectionDay = new Date(test.testedAt).getDate()
            const sampleFirstName = test.ParticipantMembers[0].User.firstName
            const sampleLastName = test.ParticipantMembers[0].User.lastName

            const fileName = `${sampleCollectionYear}.${sampleCollectionMonth}.${sampleCollectionDay}_${sampleLastName}, ${sampleFirstName}.pdf`

            downloadFileFromUrl({
              url: downloadUrl,
              filename: `${fileName}.pdf`,
            })
          }
        }
      } catch {
        // ignore
      }
      setIsLoading(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testId]
  )

  React.useEffect(() => {
    if (shouldDownloadPdfOnMount) {
      void handleDownloadClick()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldDownloadPdfOnMount])

  // Check if resultVerified is true when component mounts
  React.useEffect(() => {
    const checkResultVerified = async () => {
      try {
        const { data: testData } = await apolloClient.query<GetPDFTest, GetPDFTestVariables>({
          query: GET_PDF_TEST,
          variables: { id: testId },
        })
        const { test } = testData
        // Show button when:
        // - result is manually entered AND result is verified
        // - If the result is not manually entered, show the button normally
        setShowButton(
          (test?.resultManuallyEntered === true && test?.resultVerified === true) ||
            (test?.resultManuallyEntered !== true && test?.result !== null)
        )
      } catch {
        setShowButton(false)
      }
    }

    void checkResultVerified()
  }, [apolloClient, testId])

  // Don't render the button if resultVerified is not true
  if (!showButton) {
    return null
  }

  return (
    <Button
      leftIcon={<Icon.CloudDownloadOutline size="L" />}
      appearance={appearance}
      size={size}
      isFetching={isLoading}
      onClick={handleDownloadClick}
    >
      Download pdf
    </Button>
  )
}
